import {
  WSAlert,
  WSButton,
  WSDivider,
  WSEmptyState,
  WSFlexBox,
  WSGrid,
  WSList,
  WSPill,
  WSSelectOld
} from "@wingspanhq/fe-component-library";
import { IAdditionalData } from "@wingspanhq/payments/dist/interfaces";
import {
  ILineItemsAgingReportResponse,
  ReportAgingType
} from "@wingspanhq/payments/dist/interfaces/api/reports";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useUserId } from "../../../../query/hooks/helpers";
import { paymentsService } from "../../../../services/payments";
import { VerticalDivider } from "../../../../shared/components/VerticalDivider";
import { track } from "../../../../utils/analytics";
import { CardReportLoading } from "../../components/CardReportLoading/CardReportLoading";
import { DownloadReportButton } from "../../components/DownloadReportButton/DownloadReportButton";
import { useSelectColumnsModal } from "../../components/ModalSelectColumns";
import { ReportsTable } from "../../components/ReportsTable/ReportsTable";
import { useFetchReportData } from "../../hooks/useFetchReportData";
import { useReportColumns } from "../../hooks/useReportColumns";
import { ReportFormat, ReportType } from "../../types";
import { formatReportsValue } from "../../utils/formatReportsValue";
import { selectorColumnsWithAdditionalData } from "../../utils/selectorColumnsWithAdditionalData";
import { selectorTableColumns } from "../../utils/selectorTableColumns";
import { COLUMNS, LineItemAgingTableItem } from "./columns";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";

const SELECTED_COLUMNS_STORAGE_KEY =
  "REPORTS_OPEN_LINE_ITEM_AGING_SELECTED_COLUMNS_STORAGE_KEY";

export const OpenLineItemAgingReportDashboard: React.FC<{
  customFieldsData: IAdditionalData[];
}> = ({ customFieldsData }) => {
  const userId = useUserId();
  const [selectedAgeGrouping, setSelectedAgeGrouping] =
    useState<ReportAgingType>(ReportAgingType.Weekly);
  const [showCompletionAlert, setShowCompletionAlert] = useState(false);

  const modalSelectColumns = useSelectColumnsModal();

  const columns = selectorColumnsWithAdditionalData<LineItemAgingTableItem>(
    COLUMNS,
    customFieldsData
  );

  const [selectedColumns, setSelectedColumns] = useReportColumns(
    SELECTED_COLUMNS_STORAGE_KEY,
    userId,
    columns
  );

  const getColumnByKey = (key: string) =>
    columns.find(_column => key === _column.value)!;

  const isRunButtonDisabled = !selectedAgeGrouping || !selectedColumns.length;

  const request = useCallback(
    async (pageSize, pageNumber) => {
      return await paymentsService.reports.openLineItemAging(
        {
          type: selectedAgeGrouping
        },
        {
          page: {
            size: pageSize,
            number: pageNumber
          }
        }
      );
    },
    [selectedAgeGrouping]
  );

  const { query, totalRowsCount, processedRowsCount } = useFetchReportData<
    ILineItemsAgingReportResponse,
    { summary: { listSize: number }; data: ILineItemsAgingReportResponse[] }
  >(["QUERY_OPEN_LINE_ITEM_AGING", { type: selectedAgeGrouping }], request);

  // Check if report has just completed
  React.useEffect(() => {
    if (
      !query.isFetching &&
      totalRowsCount > 0 &&
      processedRowsCount === totalRowsCount
    ) {
      setShowCompletionAlert(true);
    }
  }, [processedRowsCount, query.isFetching, totalRowsCount]);

  useEffect(() => {
    if (query.isFetching) {
      setShowCompletionAlert(false);
    }
  }, [query.isFetching]);

  useEffect(() => {
    setShowCompletionAlert(false);
  }, [selectedAgeGrouping]);

  const tableData = useMemo(() => {
    let result: LineItemAgingTableItem[] = [];

    if (query.data) {
      const reports = query.data || [];

      reports.forEach(({ lineItems, ...report }) => {
        lineItems.forEach(lineItem => {
          result.push({
            ...report,
            lineItem,
            key: `${report.invoiceId}-${lineItem.detail}-${lineItem.description}`
          });
        });
      });
    }

    return result;
  }, [query.data]);

  const isDownloadButtonDisabled = tableData.length === 0 || query.isFetching;

  const getReportJSON = () => {
    if (!tableData.length) {
      return [];
    }
    return [
      selectedColumns.map(getColumnByKey).map(c => c?.label),
      ...tableData
        .map(data => {
          return selectedColumns.map(column => {
            let result;
            const currentColumn = getColumnByKey(column);

            try {
              result = formatReportsValue(currentColumn?.getTableCell?.(data));
            } catch (e) {
              console.error(`REPORT ERROR! "${currentColumn?.label}": `, e);
              result = undefined;
            }

            return result;
          });
        })
        .filter(arr => arr.some(Boolean))
    ];
  };

  return (
    <>
      <WSDivider mb="M" />
      <WSGrid>
        <WSGrid.Item span={{ s: "4" }}>
          <WSSelectOld
            searchable={false}
            onChange={(v: any) => {
              setSelectedAgeGrouping(v);
            }}
            name="ageGrouping"
            value={selectedAgeGrouping as string}
            placeholder="Select age grouping"
            error={false}
            options={[
              {
                value: ReportAgingType.Weekly,
                label: "7-days"
              },
              {
                value: ReportAgingType.Monthly,
                label: "30-days"
              }
            ]}
          />
        </WSGrid.Item>
        <WSGrid.Item span={{ s: "8" }}>
          <WSFlexBox.CenterY>
            <VerticalDivider mr="XL" />
            <WSFlexBox.CenterY
              onClick={() =>
                modalSelectColumns.open({
                  selectedColumns,
                  columns,
                  onSubmit: ({ columns }) => {
                    setSelectedColumns(columns);
                  }
                })
              }
              data-testid="selectColumns"
            >
              <WSButton.Link icon="menu">Selected columns</WSButton.Link>
              <WSPill ml="M" theme="blue" text={`${selectedColumns.length}`} />
            </WSFlexBox.CenterY>

            <VerticalDivider mx="XL" />
            <WSButton.Primary
              name="runReport"
              disabled={isRunButtonDisabled}
              onAsyncClick={async () => {
                track("Report Ran", {
                  reportName: ReportType.OpenLineItemAging,
                  selectedColumnsCount: selectedColumns.length,
                  selectedColumns,
                  filters: { type: selectedAgeGrouping }
                });
                await query.refetchData();
              }}
            >
              Run report
            </WSButton.Primary>
          </WSFlexBox.CenterY>
        </WSGrid.Item>
      </WSGrid>
      <WSDivider mt="M" mb="XL" />
      <WSList gap="XL">
        <WSFlexBox.CenterY justify="flex-end">
          <DownloadReportButton
            disabled={isDownloadButtonDisabled}
            getData={getReportJSON}
            onClick={(format: ReportFormat) => {
              track("Report Downloaded", {
                reportName: ReportType.OpenLineItemAging,
                selectedColumnsCount: selectedColumns.length,
                selectedColumns,
                filters: { type: selectedAgeGrouping },
                format
              });
            }}
            fileName={`Wingspan Open Age Grouping Report`}
            sheetName={`Sheet ${selectedAgeGrouping}`}
          />
        </WSFlexBox.CenterY>

        {showCompletionAlert && (
          <WSAlert
            theme="success"
            icon="check-circle"
            onDismiss={() => setShowCompletionAlert(false)}
          >
            Report complete
          </WSAlert>
        )}

        {query.isFetching && (
          <CardReportLoading
            processedRowsCount={processedRowsCount}
            totalRowsCount={totalRowsCount}
          />
        )}

        {tableData.length ? (
          <ReportsTable<LineItemAgingTableItem>
            getGroupName={item => {
              const amount = tableData
                .filter(({ ageGroup }) => {
                  return item.data.ageGroup === ageGroup;
                })
                .reduce((acc, { lineItem }) => {
                  return acc + (lineItem?.amount || 0);
                }, 0);

              return `${item.data.ageGroup} (${formatMoney(
                amount,
                // TODO(artemk): fix type
                item.data.currency as Currency
              )} total)`;
            }}
            columns={selectorTableColumns(
              selectedColumns.map(getColumnByKey).filter(Boolean)
            )}
            tableData={tableData.map((data, i) => ({
              data,
              id: `id_${i}_${data.invoiceId}`
            }))}
          />
        ) : (
          <WSEmptyState
            type="documents"
            title="No report to show yet"
            description={
              query.data?.length === 0
                ? "No invoices available"
                : "Select a grouping type then click 'Run report'"
            }
            orientation="horizontal"
            hasBorder
          />
        )}
      </WSList>
    </>
  );
};
